export type FontSize = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge';
/** Which theme typeface to render in. Defaults to the sans body face. */
export type FontFamily = 'sans' | 'heading' | 'body' | 'mono';
export type FontWeight = 'normal' | 'semi-bold' | 'bold';
export type TextColor =
    | 'primary'
    | 'secondary'
    | 'dimmed'
    | 'placeholder'
    | 'emphasis'
    | 'muted'
    | 'destructive'
    | 'success'
    | 'highlight'
    | 'warning'
    | 'error'
    | 'info'
    | 'link'
    /** Machine-authored text — what an assistant wrote, or what it is proposing. */
    | 'assistant'
    // Takes the surrounding colour instead of pinning a token.
    | 'inherit';

export const getTextColor = (color: TextColor): string => {
    switch (color) {
        case 'inherit':
            return 'inherit';
        case 'primary':
            // Chained so an ancestor (a selected Table row) can recolour text without setting…
            return 'var(--malachite-ctx-text-primary, var(--malachite-text-primary))';
        case 'assistant':
            return 'var(--malachite-assistant-foreground)';
        case 'secondary':
            return 'var(--malachite-text-secondary)';
        case 'dimmed':
            return 'var(--malachite-text-dimmed)';
        case 'placeholder':
            return 'var(--malachite-text-placeholder)';
        case 'emphasis':
            return 'var(--malachite-text-emphasis)';
        case 'muted':
            return 'var(--malachite-text-muted)';
        case 'destructive':
            return 'var(--malachite-text-destructive)';
        case 'success':
            return 'var(--malachite-text-success)';
        case 'highlight':
            return 'var(--malachite-text-highlight)';
        case 'warning':
            return 'var(--malachite-text-warning)';
        case 'error':
            return 'var(--malachite-text-error)';
        case 'info':
            return 'var(--malachite-text-info)';
        case 'link':
            return 'var(--malachite-text-link-foreground)';
        default:
            // Reachable from a non-TextColor argument (Alert passes an AlertType); keep it on the same…
            return 'var(--malachite-ctx-text-primary, var(--malachite-text-primary))';
    }
};

// Fallbacks only — the theme var wins, so overriding `fontSizes` moves Text with it. Text's
// OWN scale: SecondaryText, CodeText and TerminalText each keep a different local getFontSize.
const FONT_SIZE_FALLBACKS: Record<FontSize, string> = {
    xsmall: '11px',
    small: '13px',
    medium: '14px',
    large: '16px',
    xlarge: '18px',
    xxlarge: '22px',
};

export const getFontSize = (size: FontSize): string =>
    `var(--malachite-font-size-${size}, ${FONT_SIZE_FALLBACKS[size] ?? FONT_SIZE_FALLBACKS.small})`;

const FONT_FAMILY_VARS: Record<FontFamily, string> = {
    sans: 'var(--font-sans)',
    heading: 'var(--font-heading)',
    body: 'var(--font-body)',
    mono: 'var(--font-mono)',
};

export const getFontFamily = (font: FontFamily): string => FONT_FAMILY_VARS[font];

export const getFontWeight = (weight: FontWeight): number => {
    switch (weight) {
        case 'normal':
            return 400;
        case 'semi-bold':
            return 500;
        case 'bold':
            return 600;
        default:
            return 400;
    }
};

